import { Link, useActionData, useLocation, useNavigation } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { queries } from "@/lib/queries";
import { readPages } from "@/lib/query";
import { requireSessionWith } from "@/lib/guards";
import { tenantPath } from "@/lib/tenants";
import { SpacePicture } from "@/components/avatar";
import { ErrorNote } from "@/components/form";
import { AskToJoin, WithdrawRequest } from "@/components/join-requests";
import { ShowMore, rowsOf } from "@/components/show-more";
import { searchOf } from "@/routes/spaces-frame";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";

/**
 * Searching for spaces, a page of its own (0085): Spaces shows no results,
 * its box is the way in. Results come as you type, after a short pause, and
 * the term goes into the address (`?q=`, replaced as it changes), so a reload
 * and Back keep it and nothing about the results is component state. The
 * keyboard's Search key applies the term at once. The api answers 20 a time;
 * "Show more" reads the next.
 *
 * Asking to join and withdrawing post to Spaces' own action, which takes both
 * intents; this route is given it too, so the page is read again in place.
 */

export async function spaceSearchLoader({ request }: LoaderFunctionArgs) {
  const query = searchOf(new URL(request.url).search);
  // An empty term finds nothing and asks the api nothing.
  await requireSessionWith(request, async () => {
    if (query) await readPages(queries.searchSpaces(query));
  });
  return null;
}

export default function SpaceSearch() {
  const query = searchOf(useLocation().search);
  const result = useActionData() as { error?: string } | undefined;

  return (
    <>
      {result?.error && <ErrorNote message={result.error} />}
      {query ? <Results query={query} /> : <p className="text-sm text-muted-foreground">Search public spaces by name.</p>}
    </>
  );
}

/** What the term found, a page at a time. The loader read the first. */
function Results({ query }: { query: string }) {
  // From the tap until the pages are read again after it, so a row still
  // showing Ask to join cannot be asked twice.
  const busy = useNavigation().formData !== undefined;
  const pages = useSuspenseInfiniteQuery(queries.searchSpaces(query));
  const results = rowsOf(pages.data, (page) => page.results, ({ tenant }) => tenant.id);

  if (results.length === 0) {
    // Search finds public spaces and the viewer's own, which is worth saying
    // only when it found nothing.
    return (
      <p className="text-sm text-muted-foreground">
        Nothing public matches “{query}”. Private spaces are invisible unless you belong to them — those you can
        only be invited to.
      </p>
    );
  }

  return (
    <>
      <ul className="-mx-2 space-y-1">
        {results.map(({ tenant, role, requested }) => (
          <li key={tenant.id}>
            <Item size="sm" className="px-2">
              <ItemMedia>
                <SpacePicture space={tenant} />
              </ItemMedia>
              <ItemContent className="min-w-0 wrap-anywhere">
                <ItemTitle className="line-clamp-2">
                  <Link to={tenantPath(tenant)} className="hover:underline">
                    {tenant.name}
                  </Link>
                </ItemTitle>
                {/* Where the viewer stands is said under the name, so the line
                    keeps its width for the name and a button. */}
                <ItemDescription>
                  {tenant.visibility}
                  {role === "owner" ? (
                    <> · <span>yours</span></>
                  ) : role ? (
                    <> · <span>already joined · {role}</span></>
                  ) : requested ? (
                    <> · <span>asked to join</span></>
                  ) : null}
                </ItemDescription>
              </ItemContent>
              {!role && (
                <ItemActions>
                  {requested ? (
                    <WithdrawRequest slug={tenant.slug} busy={busy} />
                  ) : (
                    <AskToJoin slug={tenant.slug} busy={busy} />
                  )}
                </ItemActions>
              )}
            </Item>
          </li>
        ))}
      </ul>

      <ShowMore query={pages} />
    </>
  );
}
